import {
  listUsers,
  getUser,
  createUser,
  updateUser,
  updateUserStatus,
  deleteUser,
  getPrimaryTypeName,
} from '@/domains/api/users.api';
import {
  listDocuments,
  validateDocument,
  listUserDocuments,
} from '@/domains/api/documents.api';
import {
  listAllZones,
  createZone,
  updateZone,
  deleteZone,
  listAllCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  listAllAccountTypes,
  createAccountType,
  updateAccountType,
  deleteAccountType,
} from '@/domains/api/configuration.api';
import {
  listUserPermissions,
  syncUserPermissions,
  detachUserPermission,
  fetchPermissionCatalog,
  isStaffUser,
  isSuperAdminUser,
  permissionLabel,
} from '@/domains/api/permissions.api';
import {
  listVehicleTypes,
  createVehicleType,
  listSpecializations,
  createSpecialization,
  listAvailabilitySlots,
  createAvailabilitySlot,
} from '@/domains/api/catalog.api';
import {
  listDeliveryTypes,
  createDeliveryType,
  updateDeliveryType,
  deleteDeliveryType,
  listPaymentMethods,
  createPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod,
} from '@/domains/api/delivery.api';
import type { ApiDocument, ApiUser, DocumentValidationStatus } from '@/domains/api/types';
import {
  fetchAdminDashboard as apiFetchAdminDashboard,
  fetchAdminReports as apiFetchAdminReports,
  fetchAdminOrders as apiFetchAdminOrders,
  fetchAdminDisputes as apiFetchAdminDisputes,
  createAdminDispute as apiCreateAdminDispute,
  resolveAdminDispute as apiResolveAdminDispute,
  fetchAdminPromotions as apiFetchAdminPromotions,
  createAdminPromotion as apiCreateAdminPromotion,
  updateAdminPromotion as apiUpdateAdminPromotion,
  deleteAdminPromotion as apiDeleteAdminPromotion,
  fetchAdminRelayPoints as apiFetchAdminRelayPoints,
  createAdminRelayPoint as apiCreateAdminRelayPoint,
  deleteAdminRelayPoint as apiDeleteAdminRelayPoint,
  fetchAdminSupportRequests as apiFetchAdminSupportRequests,
  updateAdminSupportRequest as apiUpdateAdminSupportRequest,
  assignAdminSupportRequest as apiAssignAdminSupportRequest,
  fetchAdminStaffMessages as apiFetchAdminStaffMessages,
  sendAdminStaffMessage as apiSendAdminStaffMessage,
  markAdminStaffMessageRead as apiMarkAdminStaffMessageRead,
  type AdminDashboardData,
  type AdminReportsData,
  type AdminOrderSummary,
  type AdminDisputeSummary,
  type AdminPromotion,
  type AdminPromotionStats,
  type AdminRelayPoint,
  type AdminSupportRequest,
  type AdminStaffMessage,
} from '@/domains/api/admin.api';

export type AdminUser = ApiUser;
export type AdminDocument = ApiDocument;

export interface AdminStats {
  total_users: number;
  active_users: number;
  pending_users: number;
  suspended_users: number;
  active_drivers: number;
  pending_documents: number;
  total_zones: number;
}

export interface ConfigZone {
  id: number;
  name: string;
}

export interface ConfigCategory {
  id: number;
  name: string;
  type: string;
}

export interface ConfigReference {
  id: number;
  name: string;
}

export async function fetchAdminStats(): Promise<AdminStats> {
  const dashboard = await apiFetchAdminDashboard();
  const s = dashboard.stats;
  return {
    total_users: s.total_users,
    active_users: s.active_users,
    pending_users: s.pending_users,
    suspended_users: s.suspended_users,
    active_drivers: s.active_drivers,
    pending_documents: s.pending_documents,
    total_zones: s.total_zones,
  };
}

export async function fetchAdminDashboard(extended = false): Promise<AdminDashboardData> {
  return apiFetchAdminDashboard(extended);
}

export async function fetchAdminReports(period: 'today' | 'week' | 'month' = 'month'): Promise<AdminReportsData> {
  return apiFetchAdminReports(period);
}

export async function fetchAdminOrders(status?: string): Promise<AdminOrderSummary[]> {
  return apiFetchAdminOrders(status);
}

export async function fetchAdminDisputes(status?: string): Promise<AdminDisputeSummary[]> {
  return apiFetchAdminDisputes(status);
}

export async function createDispute(payload: {
  order_request_id: number;
  reason: string;
  description?: string;
}): Promise<AdminDisputeSummary> {
  return apiCreateAdminDispute(payload);
}

export async function resolveDispute(
  id: number,
  payload: { resolution: string; resolution_notes?: string; new_driver_id?: number },
): Promise<AdminDisputeSummary> {
  return apiResolveAdminDispute(id, payload);
}

export async function fetchPromotions(): Promise<{ promotions: AdminPromotion[]; stats: AdminPromotionStats }> {
  const res = await apiFetchAdminPromotions();
  return { promotions: res.data, stats: res.stats };
}

export async function createPromotion(payload: Partial<AdminPromotion>): Promise<AdminPromotion> {
  return apiCreateAdminPromotion(payload);
}

export async function updatePromotion(id: number, payload: Partial<AdminPromotion>): Promise<AdminPromotion> {
  return apiUpdateAdminPromotion(id, payload);
}

export async function deletePromotion(id: number): Promise<void> {
  return apiDeleteAdminPromotion(id);
}

export async function fetchRelayPoints(): Promise<AdminRelayPoint[]> {
  return apiFetchAdminRelayPoints();
}

export async function createRelayPoint(payload: Partial<AdminRelayPoint>): Promise<AdminRelayPoint> {
  return apiCreateAdminRelayPoint(payload);
}

export async function deleteRelayPoint(id: number): Promise<void> {
  return apiDeleteAdminRelayPoint(id);
}

export async function fetchSupportRequests(status?: string): Promise<AdminSupportRequest[]> {
  return apiFetchAdminSupportRequests(status);
}

export async function updateSupportRequest(
  id: number,
  payload: { status?: string; admin_notes?: string },
): Promise<AdminSupportRequest> {
  return apiUpdateAdminSupportRequest(id, payload);
}

export async function assignSupportRequest(id: number): Promise<AdminSupportRequest> {
  return apiAssignAdminSupportRequest(id);
}

export async function fetchStaffMessages(withUserId?: number): Promise<{ messages: AdminStaffMessage[]; unread_count: number }> {
  return apiFetchAdminStaffMessages(withUserId);
}

export async function sendStaffMessage(payload: { recipient_id: number; subject: string; body: string }): Promise<AdminStaffMessage> {
  return apiSendAdminStaffMessage(payload);
}

export async function markStaffMessageRead(id: number): Promise<AdminStaffMessage> {
  return apiMarkAdminStaffMessageRead(id);
}

export type {
  AdminDashboardData,
  AdminReportsData,
  AdminOrderSummary,
  AdminDisputeSummary,
  AdminPromotion,
  AdminPromotionStats,
  AdminRelayPoint,
  AdminSupportRequest,
  AdminStaffMessage,
};

export {
  listUsers as fetchAllUsers,
  getUser as fetchUserDetail,
  createUser,
  updateUser,
  updateUserStatus,
  deleteUser,
  getPrimaryTypeName,
  listDocuments as fetchAllDocuments,
  validateDocument,
  listUserDocuments as fetchUserDocuments,
  listAllZones as fetchZones,
  createZone,
  updateZone,
  deleteZone,
  listAllCategories as fetchCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  listAllAccountTypes as fetchAccountTypes,
  createAccountType,
  updateAccountType,
  deleteAccountType,
  listUserPermissions,
  syncUserPermissions,
  detachUserPermission,
  fetchPermissionCatalog,
  isStaffUser,
  isSuperAdminUser,
  permissionLabel,
  listVehicleTypes as fetchVehicleTypes,
  createVehicleType,
  listSpecializations as fetchSpecializations,
  createSpecialization,
  listAvailabilitySlots as fetchAvailabilitySlots,
  createAvailabilitySlot,
  listDeliveryTypes as fetchDeliveryTypes,
  createDeliveryType,
  updateDeliveryType,
  deleteDeliveryType,
  listPaymentMethods as fetchPaymentMethods,
  createPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod,
};

export type { DocumentValidationStatus };
